import React, { useState, useEffect } from 'react';
import styles from './CarouselManagement.module.scss';
import Header from '../shared/header';
import { connect } from 'react-redux';
import TopBarComponenent from '../audienceManagement/TopBar';
import { makeRequest } from '../../components/utils/APIsUtils/httpsUtils';
import {
  setSelectedNavigationOption,
  setIsUserLoggedIn,
  setNotificationData,
  setErrorStateTemplate,
} from '../../redux/actions/uiActions';
import Notification from '../shared/notification';
import CarouselTable from './CarouselTable';
import DeleteModalComponent from '../audienceManagement/DeleteModal';
import {
  drawerCalculatedHeight,
  drawerOverlayCalculatedHeight,
  mobDrawerToOrignalState,
  pullbox,
} from '../utils/commonUtil';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { getLiveBannerThreshold } from '../../pages/bannerCreationWizard/liveBannerLimit';

const CarouselManagement = (props) => {
  const {
    setSelectedNavigationOption,
    setIsUserLoggedIn,
    setNotificationData,
    notificationData,
    setErrorStateTemplate,
    remoteConfig,
  } = props;

  const router = useHistory();
  const [search, setSearch] = useState('');
  const [carouselList, setCarouselList] = useState([]);
  const [carouselListCopy, setCarouselListCopy] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [drawerHeight, setDrawerHeight] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [isReorderLoading, setIsReorderLoading] = useState(false);
  const [reorderTimeout, setReorderTimeout] = useState(null);
  const liveBannerThreshold = getLiveBannerThreshold(remoteConfig);

  const fetchBannerList = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        endpoint: '/admin/api/v1/banners',
      });

      if (response && response.status === 'Success' && response.data) {
        // Map API response to table format
        const mappedBanners = response.data.map((banner, index) => ({
          id: banner.id,
          header: banner.title,
          description: banner.description,
          thumbnail: banner.bannerImageDesktop?.href || banner.bannerImageMobile?.href || '',
          publishStatus: banner.bannerState || 'Draft',
          position: index + 1,
          bannerType: banner.bannerType,
          primaryButtonText: banner.primaryLink?.title,
          primaryLink: banner.primaryLink?.href,
          secondaryButtonText: banner.secondaryLink?.title,
          secondaryLink: banner.secondaryLink?.href,
          buttonTextColor: banner.buttonTextColor,
          countriesAvailability: banner.countriesAvailability,
          createdBy: banner.createdBy,
          updatedBy: banner.updatedBy,
          createdAt: banner.createdAt,
          updatedAt: banner.updatedAt,
        }));

        setCarouselList(mappedBanners);
        setCarouselListCopy(mappedBanners);
        setErrorStateTemplate({
          variant: '',
          status: false,
        });
      } else if (response === 'ERR_NETWORK') {
        setErrorStateTemplate({
          variant: 'serverError',
          status: true,
        });
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Network Error',
          description: 'Failed to fetch banner list. Please check your connection.',
        });
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      setErrorStateTemplate({
        variant: 'serverError',
        status: true,
      });
      setNotificationData({
        show: true,
        type: 'FAILURE',
        title: 'Error',
        description: 'Failed to fetch banner list. Please try again.',
      });
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      setIsUserLoggedIn(true);
      setSelectedNavigationOption('CAROUSEL');

      await fetchBannerList();

      setTimeout(() => {
        setIsLoading(false);
      }, 350);
    };

    initialize();
  }, []);

  useEffect(() => {
    if (search && search.length >= 1) {
      let result = carouselListCopy.filter((item) =>
        item.header?.toLowerCase().includes(search.toLowerCase())
      );
      setCarouselList(result);
    } else {
      setCarouselList(carouselListCopy);
    }
  }, [search, carouselListCopy]);

  const handleAddBanner = () => {
    router.push('/banner-wizard');
  };

  const handleSearchInput = (event) => {
    setSearch(event?.target?.value || '');
  };

  const handleDeleteIconClick = (id) => {
    const banner = carouselList.find((item) => item.id === id);
    setBannerToDelete(banner);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bannerToDelete) return;

    try {
      const response = await makeRequest({
        method: 'DELETE',
        endpoint: `/admin/api/v1/banners/${bannerToDelete.id}`,
      });

      if (response && response.status === 'Success') {
        // Remove from list after successful deletion
        const updatedList = carouselList.filter((item) => item.id !== bannerToDelete.id);
        const updatedCopy = carouselListCopy.filter((item) => item.id !== bannerToDelete.id);
        setCarouselList(updatedList);
        setCarouselListCopy(updatedCopy);

        // Close drawer if the deleted banner was selected
        if (selectedBanner?.id === bannerToDelete.id) {
          closeDrawer();
        }

        setNotificationData({
          show: true,
          type: 'SUCCESS',
          title: 'Banner Image Deleted',
          description: '',
        });
      } else if (response && response.status === 'Fail') {
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Delete Failed',
          description: response.message || 'Failed to delete banner',
        });
      } else if (response === 'ERR_NETWORK') {
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Network Error',
          description: 'Failed to delete banner. Please check your connection.',
        });
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
      setNotificationData({
        show: true,
        type: 'FAILURE',
        title: 'Error',
        description: 'Failed to delete banner. Please try again.',
      });
    } finally {
      setIsDeleteModalOpen(false);
      setBannerToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setBannerToDelete(null);
  };

  const handleStatusChange = async () => {
    if (!selectedBanner) return;

    const currentStatus = selectedBanner.publishStatus;
    let newStatus;

    // Determine new status based on current status
    if (currentStatus === 'Live') {
      newStatus = 'Draft';
    } else if (currentStatus === 'Draft' || currentStatus === 'Offline') {
      // Check if we can publish (fewer live banners than the remote-config threshold)
      const liveBannersCount = carouselList.filter(
        (banner) => banner.publishStatus === 'Live'
      ).length;

      if (liveBannersCount >= liveBannerThreshold) {
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Cannot Publish',
          description: `Cannot set banner to Live state: live banner threshold of ${liveBannerThreshold} would be exceeded`,
        });
        return;
      }
      newStatus = 'Live';
    } else {
      return;
    }

    try {
      const response = await makeRequest({
        method: 'PATCH',
        endpoint: `/admin/api/v1/banners/${selectedBanner.id}/state`,
        body: JSON.stringify({ banner_state: newStatus }),
      });

      if (response && response.status === 'Success') {
        // Update local state directly instead of refetching, for an instant list update.
        // The live carousel sequence is encoded by array order (live banners first, in
        // sequence order), so move the banner to keep that order correct instead of just
        // flipping its status in place.
        const bannerWithNewStatus = { ...selectedBanner, publishStatus: newStatus };

        const reorderList = (list) => {
          const withoutBanner = list.filter((item) => item.id !== selectedBanner.id);
          const lastLiveIndex = withoutBanner.reduce(
            (lastIndex, item, index) => (item.publishStatus === 'Live' ? index : lastIndex),
            -1
          );
          withoutBanner.splice(lastLiveIndex + 1, 0, bannerWithNewStatus);
          return withoutBanner.map((item, index) => ({ ...item, position: index + 1 }));
        };

        const updatedCopy = reorderList(carouselListCopy);
        const updatedList =
          search && search.length >= 1
            ? updatedCopy.filter((item) => item.header?.toLowerCase().includes(search.toLowerCase()))
            : updatedCopy;

        setCarouselList(updatedList);
        setCarouselListCopy(updatedCopy);
        setSelectedBanner(bannerWithNewStatus);

        // Check if it's a 207 Multi-Status response (state saved but sequence sync failed)
        const isPartialSuccess = response.message?.includes('carousel sequence could not be updated');

        console.log('nwStatus', newStatus);

        const defaultTitle =
          newStatus === 'Draft'
            ? 'Banner has been unpublished'
            : newStatus === 'Live'
            ? 'New Banner Published Successfully'
            : response.message || '';

        setNotificationData({
          show: true,
          type: isPartialSuccess ? 'WARNING' : 'SUCCESS',
          title: defaultTitle,
          description: '',
        });
      } else if (response && response.status === 'Fail') {
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Status Update Failed',
          description: response.message || 'Failed to update banner status',
        });
      } else if (response === 'ERR_NETWORK') {
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Network Error',
          description: 'Failed to update banner status. Please check your connection.',
        });
      }
    } catch (error) {
      console.error('Error updating banner status:', error);
      setNotificationData({
        show: true,
        type: 'FAILURE',
        title: 'Error',
        description: 'Failed to update banner status. Please try again.',
      });
    }
  };

  const handleReorderBanners = async (reorderedList) => {
    // Live banners must always stay above Draft/Offline ones. Dropping a Live
    // banner below a non-Live one would otherwise leave it stranded in the
    // middle of the list, so re-partition: keep the new relative order within
    // each group, but pull all Live banners back to the top.
    const liveItems = reorderedList.filter((item) => item.publishStatus === 'Live');
    const nonLiveItems = reorderedList.filter((item) => item.publishStatus !== 'Live');
    const normalizedList = [...liveItems, ...nonLiveItems];

    // Update position numbers immediately for UI responsiveness
    const updatedList = normalizedList.map((item, index) => ({
      ...item,
      position: index + 1,
    }));

    // Get current live banners before update
    const currentLiveBanners = carouselListCopy.filter(
      (banner) => banner.publishStatus === 'Live'
    );
    const currentLiveBannerIds = currentLiveBanners.map((banner) => banner.id);

    // Get new live banners after reorder
    const newLiveBanners = updatedList.filter(
      (banner) => banner.publishStatus === 'Live'
    );
    const newLiveBannerIds = newLiveBanners.map((banner) => banner.id);

    setCarouselList(updatedList);
    setCarouselListCopy(updatedList);

    // Check if LIVE banners order has changed
    const liveBannersOrderChanged =
      currentLiveBannerIds.length > 0 &&
      currentLiveBannerIds.length === newLiveBannerIds.length &&
      !currentLiveBannerIds.every((id, index) => id === newLiveBannerIds[index]);

    if (!liveBannersOrderChanged) {
      // LIVE banners order hasn't changed, no need to call API
      return;
    }

    // Clear existing timeout if any
    if (reorderTimeout) {
      clearTimeout(reorderTimeout);
    }

    // Set new timeout for API call (throttle for 1 second)
    const timeoutId = setTimeout(async () => {
      setIsReorderLoading(true);

      try {
        // Only send LIVE banner IDs (up to 5)
        const liveBannerIds = newLiveBanners.slice(0, liveBannerThreshold).map((banner) => banner.id);

        const response = await makeRequest({
          method: 'PUT',
          endpoint: '/admin/api/v1/banners/sequence',
          body: JSON.stringify({ banner_ids: liveBannerIds }),
        });

        if (response && response.status === 'Success') {
          setNotificationData({
            show: true,
            type: 'SUCCESS',
            title: 'Banner Sequence Updated',
            description: response.message || 'Banner sequence has been updated successfully',
          });
        } else if (response && response.status === 'Fail') {
          // Revert to original order on failure
          await fetchBannerList();
          setNotificationData({
            show: true,
            type: 'FAILURE',
            title: 'Reorder Failed',
            description: response.message || 'Failed to update banner sequence',
          });
        } else if (response === 'ERR_NETWORK') {
          // Revert to original order on network error
          await fetchBannerList();
          setNotificationData({
            show: true,
            type: 'FAILURE',
            title: 'Network Error',
            description: 'Failed to update banner sequence. Please check your connection.',
          });
        }
      } catch (error) {
        console.error('Error updating banner sequence:', error);
        // Revert to original order on error
        await fetchBannerList();
        setNotificationData({
          show: true,
          type: 'FAILURE',
          title: 'Error',
          description: 'Failed to update banner sequence. Please try again.',
        });
      } finally {
        setIsReorderLoading(false);
      }
    }, 1000);

    setReorderTimeout(timeoutId);
  };

  const handleBannerClick = (bannerId) => {
    // Find banner from the existing list
    const banner = carouselList.find((item) => item.id === bannerId);
    if (banner) {
      setSelectedBanner(banner);

      // Scroll to the selected banner element
      const element = document.getElementById(bannerId);
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  const closeDrawer = () => {
    setSelectedBanner(null);
  };

  useEffect(() => {
    if (drawerHeight >= 600) {
      closeDrawer();
    }
  }, [drawerHeight]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (reorderTimeout) {
        clearTimeout(reorderTimeout);
      }
    };
  }, [reorderTimeout]);

  return (
    <div className={[styles.carousel_mgt_wrapper].join(' ')}>
      {/* Header Component */}
      <Header
        headerName="Home Page Carousel Management"
        headerXSName=""
        btnName1="Cancel"
        btnName2="Save changes"
        isHeaderXS={false}
        isBtn1={false}
        isBtn2={false}
      />
      {/* Header Component */}

      <div
        className={
          !selectedBanner
            ? [styles.carousel_wrapper].join(' ')
            : [styles.carousel_wrapper, styles.is_drawer_open].join(' ')
        }
      >
        {/* Pinned area: stays in place while the table scrolls */}
        <div className={styles.pinned_top_area}>
        {/* Info Banner */}
        <div className={styles.info_banner}>
          {/* <div className={styles.icon_wrapper}>
            <div className={styles.lightning_icon}></div>
          </div> */}
          <div className={styles.info_content}>
            {/* <div className={styles.info_title}>New Banners can&apos;t publish</div> */}
            <div className={styles.info_description}>
              <b>You can publish up to {liveBannerThreshold} live banners at a time. Any additional banners will be saved as drafts until a live banner is unpublished.</b>
            </div>
          </div>
        </div>

        {/* Topbar Starts */}
        <TopBarComponenent
          setIsCardView={() => {}}
          setIsListView={() => {}}
          isCardView={false}
          isListView={false}
          showGridListSwitch={false}
          isLearningPath={false}
          isDrawerOpen={!!selectedBanner}
          isToggleSwitch={false}
          placeholderName={'Search Banner Name'}
          setPredictedSearch={() => {}}
          predictedSearch={[]}
          searchListFunction={(e) => {
            if (e !== '') {
              setSearch(e?.target?.value?.trim());
            } else {
              setSearch('');
            }
          }}
          handleSearchInput={handleSearchInput}
          filterData={{
            tiles: [],
            filtersList: []
          }}
          handleFilterApply={() => {}}
          handleFilterCancle={() => {}}
          isOpenSubCategories={false}
          addNewButton={true}
          addNewButtonText={'Add Banner'}
          isDrawer={false}
          setDrawer={() => {}}
          searchBarFrom={'CAROUSEL'}
          newButtonHandle={handleAddBanner}
          showFilterIcon={false}
        />
        {/* Topbar Ends */}
        </div>
        {/* Pinned area ends */}

        <div className={styles.main_content_wrapper}>
          <div className={styles.inner_wrapper}>
            {/* Content Area */}
            {isLoading ? (
              <div className={styles.loading_state}>Loading...</div>
            ) : (
              <CarouselTable
                carouselList={carouselList}
                onDelete={handleDeleteIconClick}
                onReorder={handleReorderBanners}
                onBannerClick={handleBannerClick}
                isReorderLoading={isReorderLoading}
              />
            )}
          </div>

          {/* Banner Details Drawer */}
          {selectedBanner && (
            <div className={[styles.banner_drawer_container, ''].join(' ')}>
              <div
                className={styles.custom_overlay}
                style={drawerOverlayCalculatedHeight({ drawerHeight: drawerHeight })}
              ></div>
              <div
                className={styles.banner_drawer_wrapper}
                style={drawerCalculatedHeight({ drawerHeight: drawerHeight })}
              >
                <div
                  className={styles.mob_bar}
                  draggable
                  onTouchMove={(e) => {
                    pullbox({ e: e, setDrawerHeight: setDrawerHeight });
                  }}
                  onTouchEnd={() => {
                    mobDrawerToOrignalState({ setDrawerHeight: setDrawerHeight });
                  }}
                  onClick={() => {
                    closeDrawer();
                  }}
                >
                  <div className={styles.line_bar}></div>
                </div>

                {/* middle body */}
                <div className={styles.middle_container}>
                    {/* header */}
                    <div className={styles.header}>
                      <div className={styles.title}>Banner Details</div>
                      <div
                        className={styles.close_wrapper}
                        onClick={() => {
                          closeDrawer();
                        }}
                      >
                        <div className={styles.close_icon} />
                      </div>
                    </div>

                    {/* Edit Button */}
                    <div
                      onClick={() => {
                        router.push(`/banner-wizard/${selectedBanner?.id}`)
                      }}
                      className={styles.edit_banner_btn_wrapper}
                    >
                      <div className={styles.edit_icon}></div>
                      <div className={styles.txt}>Edit Banner</div>
                    </div>

                    {/* img wrapper */}
                    <div className={[styles.img_wrapper, styles.empty_state].join(' ')}>
                      {selectedBanner?.thumbnail ? (
                        <img
                          className={styles.img}
                          src={selectedBanner?.thumbnail}
                          alt="selectedBanner thumbnail"
                        />
                      ) : (
                        <div className={[styles.img, styles.empty_state_img].join(' ')}></div>
                      )}
                    </div>
                    {/* img wrapper */}

                    <div className={styles.banner_title}>{selectedBanner?.header || 'Banner Title'}</div>

                    <div className={styles.description_wrapper}>
                      <div className={styles.label}>Description</div>
                      <div className={styles.value}>
                        {selectedBanner?.description ||
                          'Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes.'}
                      </div>
                    </div>

                    {/* CTA Buttons Row */}
                    {selectedBanner?.bannerType === 'image_link' ? (
                      <div className={styles.cta_wrapper}>
                        <div className={styles.cta_column}>
                          <a
                            href={selectedBanner?.primaryLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.cta_link}
                          >
                            Link
                            <span className={styles.arrow_icon}>›</span>
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.cta_wrapper}>
                        <div className={styles.cta_column}>
                          <div className={styles.cta_label}>Primary Button</div>
                          <a
                            href={selectedBanner?.primaryLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.cta_link}
                          >
                            {selectedBanner?.primaryButtonText || ''}
                            <span className={styles.arrow_icon}>›</span>
                          </a>
                        </div>
                        {(selectedBanner?.secondaryButtonText || selectedBanner?.secondaryLink) && (
                          <div className={styles.cta_column}>
                            <div className={styles.cta_label}>Secondary Button</div>
                            <a
                              href={selectedBanner?.secondaryLink || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.cta_link}
                            >
                              {selectedBanner?.secondaryButtonText}
                              <span className={styles.arrow_icon}>›</span>
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    <div className={styles.description_wrapper}>
                      <div className={styles.label}>Country Availability</div>
                      <div className={styles.value}>
                        {selectedBanner?.countriesAvailability
                          ? Array.isArray(selectedBanner?.countriesAvailability)
                            ? selectedBanner?.countriesAvailability.join(', ')
                            : selectedBanner?.countriesAvailability
                          : '-'}
                      </div>
                    </div>

                    {/* Metadata Section - 3 columns */}
                    <div className={styles.metadata_wrapper}>
                      <div className={styles.metadata_column}>
                        <div className={styles.metadata_label}>Created By</div>
                        <div className={styles.metadata_value}>
                          {selectedBanner?.createdBy || '-'}
                        </div>
                      </div>
                      <div className={styles.metadata_column}>
                        <div className={styles.metadata_label}>Upload Date</div>
                        <div className={styles.metadata_value}>
                          {selectedBanner?.createdAt
                            ? new Date(selectedBanner?.createdAt).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })
                            : '-'}
                        </div>
                        <div className={styles.metadata_value}>
                          {selectedBanner?.createdAt
                            ? new Date(selectedBanner?.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '-'}
                        </div>
                      </div>
                      <div className={styles.metadata_column}>
                        <div className={styles.metadata_label}>Last Updated</div>
                        <div className={styles.metadata_value}>
                          {selectedBanner?.updatedBy || '-'}
                        </div>
                        <div className={styles.metadata_value}>
                          {selectedBanner?.updatedAt
                            ? new Date(selectedBanner?.updatedAt).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                              })
                            : '-'}
                        </div>
                        <div className={styles.metadata_value}>
                          {selectedBanner?.updatedAt
                            ? new Date(selectedBanner?.updatedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })
                            : '-'}
                        </div>
                      </div>
                    </div>

                    {/* Status Section */}
                    {(() => {
                      const isDraftOrOffline =
                        selectedBanner?.publishStatus === 'Draft' ||
                        selectedBanner?.publishStatus === 'Offline';
                      const liveBannersCount = carouselList.filter(
                        (banner) => banner.publishStatus === 'Live'
                      ).length;
                      const isPublishLocked = isDraftOrOffline && liveBannersCount >= liveBannerThreshold;

                      return (
                        <div
                          className={[
                            styles.banner_status,
                            isDraftOrOffline
                              ? styles.offline
                              : styles[selectedBanner?.publishStatus?.toString().toLowerCase()],
                          ].join(' ')}
                        >
                          <div className={styles.txt_wrapper}>
                            <div className={styles.txt}>Banner is </div>
                            <div className={styles.status_txt}>
                              {isDraftOrOffline
                                ? 'Unpublished.'
                                : selectedBanner?.publishStatus
                                ? selectedBanner?.publishStatus
                                : ''}
                            </div>
                          </div>
                          {!isPublishLocked && (
                            <div
                              className={styles.status_btn}
                              onClick={handleStatusChange}
                            >
                              {selectedBanner?.publishStatus === 'Live' ? 'Unpublish' :
                               selectedBanner?.publishStatus === 'Draft' ? 'Go live' :
                               selectedBanner?.publishStatus === 'Offline' ? 'Go live' :
                               'Change Status'}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <DeleteModalComponent
            closeDeleteModal={handleDeleteCancel}
            deleteCall={handleDeleteConfirm}
            modalType="DELETE"
            name="this banner image"
            type="banner image"
          />
        )}

        {notificationData?.show && <Notification />}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  notificationData: state && state.uiState && state.uiState.notificationData,
  currentUserId: state && state.uiState && state.uiState.currentUserId,
  remoteConfig: state && state.uiState && state.uiState.remoteConfig,
});

const mapDispatchToProps = (dispatch) => ({
  setIsUserLoggedIn: (options) => dispatch(setIsUserLoggedIn(options)),
  setSelectedNavigationOption: (options) => dispatch(setSelectedNavigationOption(options)),
  setNotificationData: (options) => dispatch(setNotificationData(options)),
  setErrorStateTemplate: (options) => dispatch(setErrorStateTemplate(options)),
});

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(CarouselManagement));
